import {
    type ColumnOption,
    type EmployeeSortProperty,
    columnConfigs,
    dateAdd,
    employeeNameOrderForSort,
    formatDate,
    getContractChanges,
    getEmployeeName,
    getRange,
    mergeWithDefaultColumns,
    parseDateString,
    round,
    serializeColumOptions,
    sortBy,
    toDateString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { cache } from "lit/directives/cache.js";
import "../../../elements/column-selector";
import type { ColumnSelectorChangedEvent } from "../../../elements/column-selector";
import "../../../elements/month-picker";
import "../../../elements/popover";
import "../../../elements/scroller";
import { app, appMode } from "../../../init";
import { Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../components/contract-change";
import "../employees-filter";

@customElement("ptc-employees-contracts-all")
export class EmployeesContractsAll extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/all\/contracts(\/(?<page>[^/]+))?/;

    get routeTitle() {
        return "Verträge: Alle Mitarbeiter";
    }

    private _pages = ["list", "changes"];

    @routeProperty({ arg: "page" })
    private _page: string = "list";

    private static _defaultColumns: ColumnOption[] = [
        { type: "employmentType", order: 0 },
        { type: "nominalWeeklyHours", order: 1 },
        { type: "monthlySalary", order: 2 },
        { type: "hourlySalary", order: 3 },
        { type: "vacationDays", order: 4 },
        { type: "contract_end", order: 5 },
        { type: "probationEnd", order: 6 },
        { type: "contract_start", order: 7 },
        { type: "employment_date", order: 8 },
        { type: "employedDays", order: 9 },
        { type: "sfnAdvance", order: 10 },
        { type: "bonuses", order: 11 },
        { type: "benefits", order: 12 },
    ];

    @state()
    private _displayColumns: ColumnOption[] =
        app.settings.contractsColumns.length > 0
            ? mergeWithDefaultColumns(app.settings.contractsColumns, EmployeesContractsAll._defaultColumns)
            : EmployeesContractsAll._defaultColumns;

    private get _employees() {
        return app.getFilteredEmployees();
    }

    async handleRoute() {
        if (!this._pages.includes(this._page)) {
            this.go(`employees/all/contracts/list`);
            return;
        }

        //Invalid date
        if (!this.date || !parseDateString(this.date)) {
            this.go(null, { date: toDateString(new Date()) }, true);
            return;
        }
    }

    static styles = [
        shared,
        css`
            :host {
                --table-border-color: hsl(0deg 0% 89.8%);
                --table-sticky-column-offset: 0;
            }

            th,
            td {
                border: 1px solid var(--table-border-color);
                border-inline-start-width: 0;
                border-block-start-width: 0;
                border-collapse: separate;
                padding: var(--spacing);
            }

            td {
                white-space: nowrap;
                vertical-align: middle;
            }

            th {
                width: min-content;
                min-width: 6rem;
                position: sticky;

                font-weight: bold;
            }

            th[colspan] {
                & > * {
                    position: sticky;
                    inset-inline-start: calc(var(--table-sticky-column-offset) + var(--spacing));
                    inset-inline-end: var(--spacing);
                }
            }

            tbody {
                tr:hover {
                    cursor: pointer;
                    color: var(--color-primary);
                }

                th {
                    font-weight: normal;
                }
            }

            .sticky {
                position: sticky;
                background: var(--color-bg);
                z-index: 1;
                inset-block-start: 0;
                inset-inline-start: 0;

                &::after {
                    content: "";
                    position: absolute;
                    inset-inline-end: -1.1px;
                    inset-block-end: 0;
                    width: 1px;
                    height: 100%;
                    background: var(--table-border-color);
                    pointer-events: none;
                }
            }

            thead.sticky {
                z-index: 2;

                &::after {
                    inset-inline-end: auto;
                    width: 100%;
                    height: 1px;
                }
            }

            ptc-ui-avatar {
                margin: -0.25em 0 -0.25em -0.25em;
            }

            ptc-contract-change {
                padding-left: 0.1em;
            }

            @media print {
                * {
                    font-size: 0.7rem !important;
                }

                .padded {
                    padding: 0.3em !important;
                }

                :host,
                .fullbleed {
                    display: block;
                    position: static !important;
                }

                ptc-scroller {
                    height: auto;
                }

                .employee-wrapper {
                    break-inside: avoid;
                }

                ptc-ui-avatar {
                    display: none;
                }

                .contract-row > :last-child {
                    border-right: none;
                }
            }
        `,
    ];

    private _renderList() {
        if (!app.company) {
            return;
        }
        const bonusTypes = app.company.bonusTypes;
        const benefitTypes = app.company.benefitTypes;

        const visibleColumns = sortBy(
            this._displayColumns.filter((col) => !col.disabled),
            (col) => col.order ?? Number.MAX_SAFE_INTEGER,
            "ascending"
        );

        const showBonuses = visibleColumns.some((c) => c.type === "bonuses") && bonusTypes.length > 0;
        const showBenefits = visibleColumns.some((c) => c.type === "benefits") && benefitTypes.length > 0;
        const hasGroupedColumns = showBonuses || showBenefits;

        return html`
            <div class="fullbleed vertical layout">
                <div class="center-aligning spacing horizontal layout border-bottom noprint padded">
                    <button class="small subtle" @click=${() => this.go("employees/all/contracts/changes")}>
                        <ptc-ui-icon name="icon.object.time-history"></ptc-ui-icon>
                        Vertragsänderungen
                    </button>

                    <div class="stretch"></div>

                    <ptc-column-selector
                        .columns=${this._displayColumns}
                        @changed=${(e: ColumnSelectorChangedEvent) => {
                            this._displayColumns = e.detail.columns;
                            void app.updateSettings({ contractsColumns: e.detail.columns });
                        }}
                    ></ptc-column-selector>

                    <button
                        class="slim transparent"
                        title="Vertragsdaten als Excel-Datei Exportieren"
                        @click="${() => {
                            this.go("exports/employee-table-excel", {
                                "entity-filters": JSON.stringify(app.employeeFilters.filters),
                                columns: serializeColumOptions([{ type: "fullId" }, ...this._displayColumns]),
                            });
                        }}"
                    >
                        <ptc-ui-icon name="icon.file.excel"></ptc-ui-icon>
                    </button>

                    <button
                        class="transparent slim"
                        title="Vertragsdaten als PDF-Datei exportieren"
                        @click=${() => print()}
                        ?hidden=${appMode === "embedded"}
                    >
                        <ptc-ui-icon name="icon.action.print"></ptc-ui-icon>
                    </button>
                </div>

                <ptc-employees-filter
                    class="border-bottom"
                    .sortableProps=${[
                        "firstName",
                        "lastName",
                        "staffNumber",
                        "contractEnd",
                        "employedDays",
                    ] as EmployeeSortProperty[]}
                ></ptc-employees-filter>

                <div class="padded bottom-margined horizontal layout printonly small-caps">
                    <div class="stretch">Vertragsdaten (Stand ${formatDate(new Date())})</div>
                    <div class="subtle">${app.company?.name}</div>
                </div>
                <ptc-scroller class="stretch">
                    <table>
                        <thead class="sticky">
                            <tr>
                                <th scope="col" rowspan=${hasGroupedColumns ? "2" : "1"} class="sticky"></th>
                                ${visibleColumns.map((col) => {
                                    if (col.type === "bonuses" && showBonuses) {
                                        return html`<th id="bonusTypes" scope="colgroup" colspan=${bonusTypes.length}>
                                            <span>${columnConfigs.bonuses.getHeader()}</span>
                                        </th>`;
                                    } else if (col.type === "benefits" && showBenefits) {
                                        return html`<th
                                            id="benefitTypes"
                                            scope="colgroup"
                                            colspan=${benefitTypes.length}
                                        >
                                            <span>${columnConfigs.benefits.getHeader()}</span>
                                        </th>`;
                                    } else if (col.type !== "bonuses" && col.type !== "benefits") {
                                        return html`<th
                                            id="${col.type}"
                                            scope="col"
                                            rowspan=${hasGroupedColumns ? "2" : "1"}
                                        >
                                            ${columnConfigs[col.type].getHeader()}
                                        </th>`;
                                    }
                                    return null;
                                })}
                            </tr>
                            ${hasGroupedColumns
                                ? html`<tr>
                                      ${visibleColumns.flatMap((col) => {
                                          if (col.type === "bonuses" && showBonuses) {
                                              return bonusTypes.map(
                                                  (bonusType) =>
                                                      html`<th
                                                          id="bonusType-${bonusType.id}"
                                                          headers="bonusTypes"
                                                          scope="col"
                                                          class="padded smaller"
                                                      >
                                                          ${bonusType.name}
                                                      </th>`
                                              );
                                          } else if (col.type === "benefits" && showBenefits) {
                                              return benefitTypes.map(
                                                  (benefitType) =>
                                                      html`<th
                                                          id="benefitType-${benefitType.id}"
                                                          headers="benefitTypes"
                                                          scope="col"
                                                          class="padded smaller"
                                                      >
                                                          ${benefitType.name}
                                                      </th>`
                                              );
                                          }
                                          return [];
                                      })}
                                  </tr>`
                                : null}
                        </thead>
                        <tbody>
                            ${this._employees.map((employee) => {
                                const { currentContract } = employee;
                                if (!currentContract) {
                                    return;
                                }
                                return html`
                                    <tr @click=${() => this.go(`employees/${employee.id}/contracts`)}>
                                        <th id="employee-${employee.id}" scope="row" headers="employee" class="sticky">
                                            <div class="horizontal spacing center-aligning layout">
                                                <ptc-ui-avatar
                                                    class="tinier"
                                                    .employee=${employee}
                                                    .positionColors=${employee.positions.map((p) =>
                                                        app.company.getPositionColor(p)
                                                    )}
                                                ></ptc-ui-avatar>
                                                <div class="stretch ellipsis">
                                                    ${getEmployeeName(
                                                        employee,
                                                        employeeNameOrderForSort(app.employeeFilters.sortProperty)
                                                    )}
                                                </div>
                                                ${employee.staffNumber
                                                    ? html`<div class="small subtle">#${employee.staffNumber}</div>`
                                                    : ""}
                                            </div>
                                        </th>
                                        ${visibleColumns.flatMap((col) => {
                                            if (col.type === "bonuses" && showBonuses) {
                                                return bonusTypes.map((bonusType) => {
                                                    const bonus = currentContract.bonuses.find(
                                                        (b) => b.typeId === bonusType.id
                                                    );
                                                    return html`<td
                                                        headers="employee-${employee.id} bonusType-${bonusType.id} bonusTypes"
                                                    >
                                                        ${bonus ? `${round(bonus.percent, 2)}%` : ""}
                                                    </td>`;
                                                });
                                            } else if (col.type === "benefits" && showBenefits) {
                                                return benefitTypes.map((benefitType) => {
                                                    const benefit = currentContract.benefits.find(
                                                        (b) => b.typeId === benefitType.id
                                                    );
                                                    return html`<td
                                                        headers="employee-${employee.id} benefitType-${benefitType.id} benefitTypes"
                                                    >
                                                        ${benefit ? `${benefit.amount}€` : ""}
                                                    </td>`;
                                                });
                                            } else if (col.type !== "bonuses" && col.type !== "benefits") {
                                                return [
                                                    html`<td headers="employee-${employee.id} ${col.type}">
                                                        ${columnConfigs[col.type].getValue({ employee }, col)}
                                                    </td>`,
                                                ];
                                            }
                                            return [];
                                        })}
                                    </tr>
                                `;
                            })}
                        </tbody>
                    </table>
                </ptc-scroller>
            </div>
        `;
    }

    private _renderChanges() {
        const date = parseDateString(this.date);

        if (!date) {
            return;
        }

        const range = getRange(this.date, "month");

        const changes = this._employees
            .map((employee) => ({ employee, changes: getContractChanges(app.company!, employee, range) }))
            .filter(({ changes }) => changes.length);

        return html`
            <div class="fullbleed vertical layout">
                <div class="center-aligning padded spacing horizontal layout border-bottom noprint">
                    <button class="transparent slim" @click=${() => this.go("employees/all/contracts/list")}>
                        <ptc-ui-icon name="icon.navigation.back"></ptc-ui-icon> Verträge
                    </button>

                    <div class="stretch"></div>

                    <ptc-month-picker
                        @change=${({ detail: { date } }: CustomEvent<{ date: string }>) => this.go(null, { date })}
                        .date=${this.date}
                    ></ptc-month-picker>

                    <div class="stretch"></div>

                    <button class="transparent slim" @click=${() => print()} ?hidden=${appMode === "embedded"}>
                        <ptc-ui-icon name="icon.action.print"></ptc-ui-icon>
                    </button>
                </div>

                <ptc-employees-filter
                    class="border-bottom"
                    .sortableProps=${["firstName", "lastName", "staffNumber"] as EmployeeSortProperty[]}
                ></ptc-employees-filter>

                <div class="padded horizontaly layout small-caps printonly bottom-margined">
                    <div class="stretch">
                        Vertragsänderungen
                        <strong>${formatDate(range.from)} - ${formatDate(dateAdd(range.to, { days: -1 }))}</strong>
                    </div>
                    <div class="subtle">${app.company?.name}</div>
                </div>

                ${!changes.length
                    ? html`
                          <div class="stretch faded double-padded centering vertical layout">
                              <ptc-ui-icon name="icon.object.time-history" class="giant"></ptc-ui-icon>
                              <div>Es liegen keine Vertragsänderungen für diesen Monat vor.</div>
                          </div>
                      `
                    : html`
                          <ptc-scroller class="stretch">
                              ${this._employees.map((employee) => {
                                  const contractChanges = getContractChanges(app.company!, employee, range);
                                  if (!contractChanges.length) {
                                      return;
                                  }
                                  return html`
                                      <div class="padded border-bottom employee-wrapper">
                                          <div
                                              class="padded spacing horizontal center-aligning layout employee-header"
                                              @click=${() => this.go(`employees/${employee.id}/contracts/changes`)}
                                          >
                                              <ptc-ui-avatar
                                                  .employee=${employee}
                                                  class="tinier"
                                                  .positionColors=${employee.positions.map((p) =>
                                                      app.company.getPositionColor(p)
                                                  )}
                                              ></ptc-ui-avatar>
                                              <div class="stretch semibold">
                                                  ${employee.name}
                                                  ${employee.staffNumber
                                                      ? html`
                                                            <span class="small subtle">#${employee.staffNumber}</span>
                                                        `
                                                      : ""}
                                              </div>
                                          </div>
                                          ${contractChanges.map(
                                              (change) => html`
                                                  <ptc-contract-change .change=${change}></ptc-contract-change>
                                              `
                                          )}
                                      </div>
                                  `;
                              })}
                          </ptc-scroller>
                      `}
            </div>
        `;
    }

    render() {
        return cache(this._page === "changes" ? this._renderChanges() : this._renderList());
    }

    updated() {
        queueMicrotask(() => {
            // Set sticky column offset based on first column width
            const firstColumn = this.shadowRoot?.querySelector("th.sticky");
            if (firstColumn) {
                const width = firstColumn.getBoundingClientRect().width;
                this.style.setProperty("--table-sticky-column-offset", `${width}px`);
            }
        });
    }
}
